import { useTranslation } from 'react-i18next'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { useWalletTypes } from '@/hooks/useWalletTypes'

/**
 * Sentinel for the "company fallback wallet" option. The backend models that as `null`
 * (resolved to the company's fallback wallet at booking time), but a shadcn `SelectItem`
 * needs a non-empty string value — so it is mapped back to `null` on change.
 */
const FALLBACK_OPTION_VALUE = 'fallback'

interface Props {
  /** Wallet type id, or `null` for the company fallback wallet. */
  value: string | null | undefined
  onChange: (walletTypeId: string | null) => void
  disabled?: boolean
  /** Required in admin scope (the module belongs to another company); omit in CMU scope. */
  companyId?: number
}

/**
 * Picks one of the company's wallet types, or the company fallback wallet. Fetches the list
 * itself so call sites only deal with the id. While the list loads the select is disabled —
 * otherwise it would offer nothing but the fallback option.
 */
export function WalletTypeSelect({ value, onChange, disabled, companyId }: Props) {
  const { t } = useTranslation('common')
  const { walletTypes, isLoading } = useWalletTypes(companyId)

  const fallbackWalletName = walletTypes.find((walletType) => walletType.isFallback)?.name
  const fallbackOptionLabel = fallbackWalletName
    ? t('walletType.fallbackOption', { name: fallbackWalletName })
    : t('walletType.fallbackOptionUnnamed')
  const explicitWalletTypes = walletTypes.filter((walletType) => !walletType.isFallback)

  return (
    <Select
      value={value ?? FALLBACK_OPTION_VALUE}
      onValueChange={(v) => onChange(v === FALLBACK_OPTION_VALUE ? null : v)}
      disabled={disabled || isLoading}
    >
      <SelectTrigger className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={FALLBACK_OPTION_VALUE}>{fallbackOptionLabel}</SelectItem>
        {explicitWalletTypes.map((walletType) => (
          <SelectItem key={walletType.id} value={walletType.id}>
            {walletType.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
